import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtErr } from "../api";
import { Badge } from "../Badge";
import { PATIENT_STATUSES } from "./Patients";
import { SUPPORT_OPTIONS } from "../../constants/data";
import { ArrowLeft, Send, Archive, ArchiveRestore } from "lucide-react";

const inputCls = "w-full rounded-xl border border-slate-300 px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-teal/60 focus:border-brand-teal";
const labelCls = "block text-sm font-bold text-brand-navy";

const Field = ({ label, name, form, set, type = "text" }) => (
  <div>
    <label htmlFor={name} className={labelCls}>{label}</label>
    <input id={name} data-testid={`patient-${name}`} type={type} value={form[name] || ""} onChange={(e) => set(name, e.target.value)} className={`mt-2 ${inputCls}`} />
  </div>
);

export default function PatientDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [form, setForm] = useState({});
  const [plan, setPlan] = useState(null);
  const [note, setNote] = useState("");
  const [portalPw, setPortalPw] = useState("");

  const load = () => api.get(`/admin/patients/${id}`).then((res) => {
    setP(res.data);
    setForm(res.data);
    setPlan(res.data.care_plan || {
      services: [], instructions: "", preferences: "", considerations: "",
      mobility_safety: "", visit_frequency: "", preferred_days: "", preferred_times: "", caregiver_requirements: "",
    });
  }).catch(() => setP(false));

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (p === null) return <p className="text-slate-500 font-semibold">Loading patient…</p>;
  if (p === false) return <p className="text-red-700 font-semibold">Patient not found.</p>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPlanField = (k, v) => setPlan((pl) => ({ ...pl, [k]: v }));

  const saveInfo = async () => {
    try {
      await api.patch(`/admin/patients/${id}`, {
        full_name: form.full_name, dob: form.dob, phone: form.phone, email: form.email, address: form.address,
        emergency_name: form.emergency_name, emergency_relationship: form.emergency_relationship,
        emergency_phone: form.emergency_phone, emergency_email: form.emergency_email,
        referrer_name: form.referrer_name, referrer_clinic: form.referrer_clinic,
        referrer_phone: form.referrer_phone, referrer_email: form.referrer_email,
        care_considerations: form.care_considerations,
        preferred_start_date: form.preferred_start_date, preferred_days_hours: form.preferred_days_hours,
        status: form.status,
      });
      toast.success("Patient information saved.");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const savePlan = async () => {
    try {
      await api.put(`/admin/patients/${id}/careplan`, plan);
      toast.success("Care plan saved.");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    try {
      await api.post(`/admin/patients/${id}/notes`, { text: note });
      setNote("");
      toast.success("Note added.");
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const toggleArchive = async () => {
    try {
      await api.patch(`/admin/patients/${id}`, { archived: !p.archived });
      toast.success(p.archived ? "Patient restored." : "Patient archived.");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const createPortalLogin = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/patients/${id}/portal-login`, { password: portalPw });
      toast.success("Patient portal login created.");
      setPortalPw("");
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  return (
    <div data-testid="patient-detail">
      <Link to="/portal/patients" data-testid="back-to-patients" className="inline-flex items-center gap-2 text-sm font-bold text-brand-teal hover:text-brand-navy">
        <ArrowLeft className="h-4 w-4" /> Back to Patients
      </Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl md:text-4xl text-brand-navy">{p.full_name}</h1>
          <p className="mt-1 text-sm text-slate-500">
            {p.referral_id && <>Created from referral #{p.referral_id.slice(0, 8)} · </>}Added {new Date(p.created_at).toLocaleDateString()}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge kind="patient" value={p.status} />
          <button onClick={toggleArchive} data-testid="patient-archive-toggle" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2.5 text-sm font-bold text-brand-navy hover:bg-brand-mist transition-colors duration-300">
            {p.archived ? <><ArchiveRestore className="h-4 w-4" /> Restore</> : <><Archive className="h-4 w-4" /> Archive</>}
          </button>
        </div>
      </div>

      <div className="mt-6 space-y-6">
        <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
          <h2 className="font-display text-xl text-brand-navy">Patient Information</h2>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <Field label="Full Name" name="full_name" form={form} set={set} />
            <Field label="Date of Birth" name="dob" form={form} set={set} type="date" />
            <Field label="Phone Number" name="phone" form={form} set={set} />
            <Field label="Email Address" name="email" form={form} set={set} />
            <div className="sm:col-span-2"><Field label="Home Address" name="address" form={form} set={set} /></div>
            <div>
              <label htmlFor="status" className={labelCls}>Patient Status</label>
              <select id="status" data-testid="patient-status-select" value={form.status || ""} onChange={(e) => set("status", e.target.value)} className={`mt-2 ${inputCls}`}>
                {PATIENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <h3 className="mt-8 font-display text-lg text-brand-navy">Emergency / Family Contact</h3>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <Field label="Name" name="emergency_name" form={form} set={set} />
            <Field label="Relationship" name="emergency_relationship" form={form} set={set} />
            <Field label="Phone" name="emergency_phone" form={form} set={set} />
            <Field label="Email" name="emergency_email" form={form} set={set} />
          </div>
          <h3 className="mt-8 font-display text-lg text-brand-navy">Referring Healthcare Professional / Clinic</h3>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <Field label="Physician / Professional" name="referrer_name" form={form} set={set} />
            <Field label="Clinic / Organization" name="referrer_clinic" form={form} set={set} />
            <Field label="Phone" name="referrer_phone" form={form} set={set} />
            <Field label="Email" name="referrer_email" form={form} set={set} />
          </div>
          <div className="mt-6">
            <label htmlFor="care_considerations" className={labelCls}>Care Considerations</label>
            <textarea id="care_considerations" data-testid="patient-care-considerations" rows={3} value={form.care_considerations || ""} onChange={(e) => set("care_considerations", e.target.value)} className={`mt-2 ${inputCls}`} />
          </div>
          <button onClick={saveInfo} data-testid="patient-save" className="mt-6 rounded-full bg-brand-teal px-6 py-3 text-[15px] font-bold text-white hover:bg-brand-navy transition-colors duration-300 active:scale-95">
            Save Patient Information
          </button>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
          <h2 className="font-display text-xl text-brand-navy">Requested Care & Support (from referral)</h2>
          <ul className="mt-4 list-disc list-inside text-[15px] text-brand-navy space-y-1">
            {(p.requested_support || []).map((s) => <li key={s}>{s}</li>)}
            {p.other_support && <li>Other: {p.other_support}</li>}
          </ul>
          {p.reason && <p className="mt-4 text-[15px] text-slate-600"><strong className="text-brand-navy">Reason for referral:</strong> {p.reason}</p>}
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
          <h2 className="font-display text-xl text-brand-navy">Care Plan</h2>
          <p className="mt-1 text-xs text-slate-500">Visible to office staff. Caregivers see only the care instructions and mobility/safety notes relevant to their assigned visits.</p>
          {plan && (
            <div className="mt-5 space-y-5">
              <div>
                <p className={labelCls}>Required Care Services</p>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {SUPPORT_OPTIONS.filter((o) => o !== "Other").map((o) => (
                    <label key={o} className={`flex items-center gap-2.5 rounded-xl border px-3.5 py-2.5 cursor-pointer text-sm font-semibold transition-colors duration-300 ${plan.services.includes(o) ? "border-brand-teal bg-brand-teal/5 text-brand-navy" : "border-slate-300 text-slate-600 hover:border-brand-teal/50"}`}>
                      <input
                        type="checkbox"
                        data-testid={`plan-service-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        checked={plan.services.includes(o)}
                        onChange={() => setPlanField("services", plan.services.includes(o) ? plan.services.filter((s) => s !== o) : [...plan.services, o])}
                        className="h-4 w-4 accent-[#008080]"
                      />
                      {o}
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="plan-instructions" className={labelCls}>Care Instructions</label>
                  <textarea id="plan-instructions" data-testid="plan-instructions" rows={3} value={plan.instructions} onChange={(e) => setPlanField("instructions", e.target.value)} className={`mt-2 ${inputCls}`} />
                </div>
                <div>
                  <label htmlFor="plan-preferences" className={labelCls}>Patient Preferences</label>
                  <textarea id="plan-preferences" data-testid="plan-preferences" rows={3} value={plan.preferences} onChange={(e) => setPlanField("preferences", e.target.value)} className={`mt-2 ${inputCls}`} />
                </div>
                <div>
                  <label htmlFor="plan-considerations" className={labelCls}>Important Care Considerations</label>
                  <textarea id="plan-considerations" data-testid="plan-considerations" rows={3} value={plan.considerations} onChange={(e) => setPlanField("considerations", e.target.value)} className={`mt-2 ${inputCls}`} />
                </div>
                <div>
                  <label htmlFor="plan-mobility" className={labelCls}>Mobility or Safety Requirements</label>
                  <textarea id="plan-mobility" data-testid="plan-mobility" rows={3} value={plan.mobility_safety} onChange={(e) => setPlanField("mobility_safety", e.target.value)} className={`mt-2 ${inputCls}`} />
                </div>
                <div>
                  <label htmlFor="plan-frequency" className={labelCls}>Visit Frequency</label>
                  <input id="plan-frequency" data-testid="plan-frequency" type="text" placeholder="e.g. 3 visits per week" value={plan.visit_frequency} onChange={(e) => setPlanField("visit_frequency", e.target.value)} className={`mt-2 ${inputCls}`} />
                </div>
                <div>
                  <label htmlFor="plan-days" className={labelCls}>Preferred Visit Days</label>
                  <input id="plan-days" data-testid="plan-days" type="text" placeholder="e.g. Mon, Wed, Fri" value={plan.preferred_days} onChange={(e) => setPlanField("preferred_days", e.target.value)} className={`mt-2 ${inputCls}`} />
                </div>
                <div>
                  <label htmlFor="plan-times" className={labelCls}>Preferred Visit Times</label>
                  <input id="plan-times" data-testid="plan-times" type="text" placeholder="e.g. Mornings" value={plan.preferred_times} onChange={(e) => setPlanField("preferred_times", e.target.value)} className={`mt-2 ${inputCls}`} />
                </div>
                <div>
                  <label htmlFor="plan-caregiver-req" className={labelCls}>Assigned Caregiver Requirements</label>
                  <input id="plan-caregiver-req" data-testid="plan-caregiver-req" type="text" placeholder="e.g. Punjabi-speaking preferred" value={plan.caregiver_requirements} onChange={(e) => setPlanField("caregiver_requirements", e.target.value)} className={`mt-2 ${inputCls}`} />
                </div>
              </div>
              <button onClick={savePlan} data-testid="plan-save" className="rounded-full bg-brand-teal px-6 py-3 text-[15px] font-bold text-white hover:bg-brand-navy transition-colors duration-300 active:scale-95">
                Save Care Plan
              </button>
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
          <h2 className="font-display text-xl text-brand-navy">Assigned Caregivers & Scheduled Visits</h2>
          <p className="mt-3 text-[15px] text-slate-600">
            <strong className="text-brand-navy">Caregivers:</strong> {(p.assigned_caregivers || []).length ? p.assigned_caregivers.join(", ") : "None assigned yet"}
          </p>
          <ul className="mt-4 space-y-3">
            {(p.visits || []).length === 0 && <li className="text-sm text-slate-500">No visits scheduled for this patient yet.</li>}
            {(p.visits || []).map((v) => (
              <li key={v.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-100 px-4 py-3">
                <div>
                  <p className="text-sm font-bold text-brand-navy">{v.date} · {v.start_time} – {v.end_time} · {v.care_type}</p>
                  <p className="text-xs text-slate-500">Caregiver: {v.caregiver_name}</p>
                </div>
                <Badge kind="visit" value={v.status} />
              </li>
            ))}
          </ul>
          <Link to="/portal/schedule" data-testid="patient-schedule-link" className="mt-4 inline-block text-sm font-bold text-brand-teal hover:text-brand-navy">
            Go to Schedule to create a visit →
          </Link>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
          <h2 className="font-display text-xl text-brand-navy">Patient Portal Access</h2>
          {p.has_portal_login ? (
            <p data-testid="portal-login-enabled" className="mt-3 text-[15px] text-slate-600">
              Portal login is <strong className="text-brand-teal">enabled</strong> for this patient ({p.email}). The patient or their authorized representative can sign in at /patient/login and reset their password via Forgot Password.
            </p>
          ) : (
            <div className="mt-3">
              <p className="text-[15px] text-slate-600">Create a secure portal login so the patient or their authorized representative can view their schedule and care plan. The login email will be <strong className="text-brand-navy">{p.email}</strong>.</p>
              <form onSubmit={createPortalLogin} className="mt-4 flex flex-wrap gap-3">
                <input
                  type="text"
                  data-testid="portal-login-password"
                  value={portalPw}
                  onChange={(e) => setPortalPw(e.target.value)}
                  placeholder="Temporary password (min 8 characters)"
                  className="flex-1 min-w-[240px] rounded-xl border border-slate-300 px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-teal/60"
                />
                <button type="submit" data-testid="portal-login-create" className="rounded-full bg-brand-navy px-6 py-3 text-[15px] font-bold text-white hover:bg-brand-teal transition-colors duration-300 active:scale-95">
                  Create Portal Login
                </button>
              </form>
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
          <h2 className="font-display text-xl text-brand-navy">Internal Notes</h2>
          <ul className="mt-4 space-y-3">
            {(p.internal_notes || []).length === 0 && <li className="text-sm text-slate-500">No notes yet.</li>}
            {(p.internal_notes || []).map((n) => (
              <li key={n.id} className="rounded-xl bg-brand-mist/60 px-4 py-3">
                <p className="text-[15px] text-brand-navy">{n.text}</p>
                <p className="mt-1 text-xs text-slate-500">{n.author} · {new Date(n.created_at).toLocaleString()}</p>
              </li>
            ))}
          </ul>
          <form onSubmit={addNote} className="mt-5 flex gap-3">
            <input data-testid="patient-note-input" type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a private note…" className="flex-1 rounded-xl border border-slate-300 px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-teal/60" />
            <button type="submit" data-testid="patient-note-add" className="inline-flex items-center gap-2 rounded-full bg-brand-teal px-5 py-3 text-sm font-bold text-white hover:bg-brand-navy transition-colors duration-300 active:scale-95">
              <Send className="h-4 w-4" /> Add Note
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}
